import React, { useEffect, useState } from 'react';
import { useAuth } from './contexts/AuthContext';
import { useAlert } from './contexts/AlertContext';
import apiService from './apiService';

const SuperAdminDashboard = () => {
    const { currentUser } = useAuth();
    const { showAlert } = useAlert();

    const [organizations, setOrganizations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [newOrg, setNewOrg] = useState({ name: '', orgType: 'solo', adminUsername: '', adminPassword: '', adminEmail: '' });

    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                const list = await apiService.getOrganizations();
                if (mounted) setOrganizations(list);
            } catch (e) {
                showAlert('Failed to load organizations', 'error');
            } finally {
                if (mounted) setLoading(false);
            }
        })();
        return () => { mounted = false; };
    }, [showAlert]);

    const handleCreateOrg = async (e) => {
        e.preventDefault();
        try {
            const created = await apiService.createOrganization({ 
                name: newOrg.name, 
                orgType: newOrg.orgType,
                adminUsername: newOrg.adminUsername,
                adminPassword: newOrg.adminPassword,
                adminEmail: newOrg.adminEmail
            });
            setOrganizations([created, ...organizations]);
            setNewOrg({ name: '', orgType: 'solo', adminUsername: '', adminPassword: '', adminEmail: '' });
            showAlert('Organization created', 'success');
        } catch (e) {
            showAlert('Failed to create organization', 'error');
        }
    };

    if (currentUser?.role !== 'superadmin') {
        return <div className="p-6 text-red-600">Access denied.</div>;
    }

    return (
        <div className="space-y-6">
            <div>
                <h2 className="text-2xl font-bold">System Administration</h2>
                <p className="text-gray-600 dark:text-gray-400">Manage organizations and system-wide settings</p>
            </div>
            
            <div className="bg-white dark:bg-gray-800 p-4 rounded-lg">
                <h3 className="text-lg font-semibold mb-4">Create New Organization</h3>
            <form onSubmit={handleCreateOrg} className="space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <input type="text" required placeholder="Organization name" value={newOrg.name} onChange={(e) => setNewOrg({ ...newOrg, name: e.target.value })} className="border p-2 rounded" />
                    <select value={newOrg.orgType} onChange={(e) => setNewOrg({ ...newOrg, orgType: e.target.value })} className="border p-2 rounded">
                        <option value="solo">Solo Practice</option>
                        <option value="clinic">Clinic</option>
                        <option value="hospital">Hospital</option>
                        <option value="pharmacy">Pharmacy</option>
                    </select>
                    <input type="text" required placeholder="Admin username" value={newOrg.adminUsername} onChange={(e) => setNewOrg({ ...newOrg, adminUsername: e.target.value })} className="border p-2 rounded" />
                    <input type="password" required placeholder="Admin password" value={newOrg.adminPassword} onChange={(e) => setNewOrg({ ...newOrg, adminPassword: e.target.value })} className="border p-2 rounded" />
                    <input type="email" required placeholder="Admin email" value={newOrg.adminEmail} onChange={(e) => setNewOrg({ ...newOrg, adminEmail: e.target.value })} className="border p-2 rounded" />
                    <button type="submit" className="bg-blue-600 text-white px-4 py-2 rounded">Create Organization</button>
                </div>
            </form>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg">
                <h3 className="text-lg font-semibold p-4 border-b">Organizations</h3>
                {loading ? (
                    <div className="p-6">Loading organizations…</div>
                ) : (
                    <table className="w-full text-left">
                        <thead className="bg-gray-50 dark:bg-gray-700">
                            <tr className="border-b">
                                <th className="p-3">Name</th>
                                <th className="p-3">Type/Plan</th>
                                <th className="p-3">Subscription</th>
                                <th className="p-3">Users</th>
                                <th className="p-3">Created</th>
                            </tr>
                        </thead>
                        <tbody>
                            {organizations.map(o => {
                                const planConfig = {
                                    solo: { price: 20, name: 'Solo Practice' },
                                    clinic: { price: 40, name: 'Clinic' },
                                    hospital: { price: 100, name: 'Hospital' },
                                    pharmacy: { price: 35, name: 'Pharmacy' }
                                };
                                const plan = planConfig[o.org_type || o.orgType] || planConfig.clinic;
                                
                                return (
                                    <tr key={o.id} className="border-b hover:bg-gray-50 dark:hover:bg-gray-700">
                                        <td className="p-3 font-semibold">{o.name}</td>
                                        <td className="p-3">
                                            <div>
                                                <span className="font-medium capitalize">{plan.name}</span>
                                                <span className="text-sm text-gray-500 block">${plan.price}/mo</span>
                                            </div>
                                        </td>
                                        <td className="p-3">
                                            <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">Trial</span>
                                        </td>
                                        <td className="p-3">{o.numUsers ?? '-'}</td>
                                        <td className="p-3">{o.created_at ? new Date(o.created_at).toLocaleDateString() : '-'}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
};

export default SuperAdminDashboard;


